/**
 * StandalonePayment Page
 *
 * Payment page for standalone payment links.
 * Two-step flow: register student data -> pay via WebPay.
 *
 * Route: /pago/link/:token
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  CreditCard,
  User,
  Mail,
  Phone,
  FileText,
  MapPin,
  Calendar,
  ArrowLeft,
  ShieldCheck,
  ArrowRight
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getLinkData, registerStudent, createStandalonePayment, redirectToWebPay } from '../services/standalonePaymentApi';
import { formatCLP } from '../utils/priceFormat';
import { validateRut, formatRut } from '../utils/rutValidation';
import { validateChileanPhone, formatChileanPhone } from '../utils/phoneValidation';

export default function StandalonePayment() {
  const { token } = useParams();
  const navigate = useNavigate();

  // Page state
  const [isLoading, setIsLoading] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isAlreadyPaid, setIsAlreadyPaid] = useState(false);

  // Step: 'register' or 'payment'
  const [step, setStep] = useState('register');

  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [rut, setRut] = useState('');
  const [phone, setPhone] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isRegistering, setIsRegistering] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch link data on mount
  useEffect(() => {
    async function fetchData() {
      if (!token) {
        setLoadError('Token de pago no proporcionado.');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getLinkData(token);
        setLinkData(data);

        // If student already registered, go to payment step
        if (data.paymentStatus === 'registered' && data.studentName) {
          setFullName(data.studentName);
          setEmail(data.email);
          setRut(data.rut);
          setPhone(data.phone);
          setStep('payment');
        }
      } catch (err) {
        console.error('Error fetching link data:', err);
        setLoadError(err.message);
        if (err.expired || err.status === 410) setIsExpired(true);
        if (err.alreadyPaid) setIsAlreadyPaid(true);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [token]);

  // Real-time field validation on blur
  const handleRutBlur = () => {
    if (rut.trim()) {
      const result = validateRut(rut.trim());
      if (result.isValid && result.formatted) {
        setRut(result.formatted);
        setFormErrors(prev => ({ ...prev, rut: undefined }));
      } else {
        setFormErrors(prev => ({ ...prev, rut: result.error || 'RUT inválido' }));
      }
    }
  };

  const handlePhoneBlur = () => {
    if (phone.trim()) {
      const phoneToValidate = phone.trim().startsWith('+56') || phone.trim().startsWith('56')
        ? phone.trim()
        : `+56${phone.trim().replace(/\s/g, '')}`;
      const result = validateChileanPhone(phoneToValidate);
      if (result.isValid && result.formatted) {
        setPhone(result.formatted);
        setFormErrors(prev => ({ ...prev, phone: undefined }));
      } else {
        setFormErrors(prev => ({ ...prev, phone: result.error || 'Teléfono inválido' }));
      }
    }
  };

  const handleEmailBlur = () => {
    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setFormErrors(prev => ({ ...prev, email: 'Ingrese un email válido' }));
      } else {
        setFormErrors(prev => ({ ...prev, email: undefined }));
      }
    }
  };

  // Clear field error on change
  const handleFieldChange = (setter, field) => (e) => {
    setter(e.target.value);
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Validate all fields before submit
  const validate = () => {
    const errors = {};

    if (!fullName.trim() || fullName.trim().length < 3) {
      errors.fullName = 'Ingrese su nombre completo (mínimo 3 caracteres)';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errors.email = 'El email es requerido';
    } else if (!emailRegex.test(email.trim())) {
      errors.email = 'Ingrese un email válido';
    }

    if (!rut.trim()) {
      errors.rut = 'El RUT es requerido';
    } else {
      const rutResult = validateRut(rut.trim());
      if (!rutResult.isValid) {
        errors.rut = rutResult.error || 'RUT inválido';
      }
    }

    if (!phone.trim()) {
      errors.phone = 'El teléfono es requerido';
    } else {
      const phoneToValidate = phone.trim().startsWith('+56') || phone.trim().startsWith('56')
        ? phone.trim()
        : `+56${phone.trim().replace(/\s/g, '')}`;
      const phoneResult = validateChileanPhone(phoneToValidate);
      if (!phoneResult.isValid) {
        errors.phone = phoneResult.error || 'Teléfono inválido';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle registration
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsRegistering(true);
    setActionError(null);

    try {
      const rutResult = validateRut(rut.trim());
      const phoneToValidate = phone.trim().startsWith('+56') || phone.trim().startsWith('56')
        ? phone.trim()
        : `+56${phone.trim().replace(/\s/g, '')}`;
      const phoneResult = validateChileanPhone(phoneToValidate);

      await registerStudent(token, {
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        rut: rutResult.formatted,
        phone: phoneResult.formatted,
      });
      setStep('payment');
    } catch (err) {
      console.error('Registration error:', err);
      setActionError(err.message);
      if (err.expired) setIsExpired(true);
    } finally {
      setIsRegistering(false);
    }
  };

  // Handle payment
  const handlePayment = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setActionError(null);

    try {
      const result = await createStandalonePayment(token);
      if (result.success && result.url && result.token) {
        redirectToWebPay(result.url, result.token);
      } else {
        throw new Error('Error al crear la transacción de pago.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setActionError(err.message);
      setIsProcessing(false);
      if (err.expired) setIsExpired(true);
      if (err.alreadyPaid) setIsAlreadyPaid(true);
    }
  };

  const formatExpirationDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Header />
      <section className="min-h-screen py-24 bg-slate-900 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent" />
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20">

              {/* Loading */}
              {isLoading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                  <p className="text-slate-400">Cargando información del pago...</p>
                </motion.div>
              )}

              {/* Error: Expired */}
              {!isLoading && isExpired && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Enlace Expirado</h2>
                  <p className="text-slate-400 mb-6">
                    Este enlace de pago ha expirado. Contacta al instituto para obtener un nuevo enlace.
                  </p>
                  <p className="text-sm text-slate-500 mb-6">
                    WhatsApp: +56 9 7548 3729
                  </p>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/')}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/20 transition-all flex items-center gap-2 mx-auto"
                  >
                    <ArrowLeft className="w-5 h-5" /> Volver al Inicio
                  </motion.button>
                </motion.div>
              )}

              {/* Error: Already Paid */}
              {!isLoading && isAlreadyPaid && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Pago Ya Realizado</h2>
                  <p className="text-slate-400 mb-6">Este enlace ya fue utilizado para completar un pago.</p>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/')}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/20 transition-all flex items-center gap-2 mx-auto"
                  >
                    <ArrowLeft className="w-5 h-5" /> Volver al Inicio
                  </motion.button>
                </motion.div>
              )}

              {/* Error: Generic (load error only) */}
              {!isLoading && loadError && !isExpired && !isAlreadyPaid && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Error</h2>
                  <p className="text-slate-400 mb-6">{loadError}</p>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/')}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/20 transition-all flex items-center gap-2 mx-auto"
                  >
                    <ArrowLeft className="w-5 h-5" /> Volver al Inicio
                  </motion.button>
                </motion.div>
              )}

              {/* Main Content */}
              {!isLoading && linkData && !isExpired && !isAlreadyPaid && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>

                  {/* Course Info */}
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                      <CreditCard className="w-7 h-7 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-1">Pago de Curso</h2>
                    <p className="text-cyan-300 font-medium">{linkData.courseName}</p>
                  </div>

                  {/* Price & Details Card */}
                  <div className="bg-white/5 rounded-2xl p-5 mb-6 border border-white/10">
                    <div className="text-center mb-3">
                      <p className="text-sm text-slate-400">Monto a pagar</p>
                      <p className="text-3xl font-bold text-white">{formatCLP(linkData.coursePrice)} <span className="text-base text-slate-400">CLP</span></p>
                    </div>
                    <div className="space-y-2 text-sm">
                      {linkData.courseDate && (
                        <div className="flex items-center gap-2 text-slate-300">
                          <Calendar className="w-4 h-4 text-cyan-400" />
                          <span>{linkData.courseDate}</span>
                        </div>
                      )}
                      {linkData.courseLocation && (
                        <div className="flex items-center gap-2 text-slate-300">
                          <MapPin className="w-4 h-4 text-cyan-400" />
                          <span>{linkData.courseLocation}</span>
                        </div>
                      )}
                      {linkData.expiresAt && (
                        <div className="flex items-center gap-2 text-amber-400 text-xs">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Enlace válido hasta: {formatExpirationDate(linkData.expiresAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Step indicator */}
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className={`flex items-center gap-1.5 text-sm ${step === 'register' ? 'text-cyan-400' : 'text-emerald-400'}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        step === 'register' ? 'bg-cyan-500/20 border border-cyan-500/50' : 'bg-emerald-500/20 border border-emerald-500/50'
                      }`}>
                        {step === 'payment' ? <CheckCircle className="w-3.5 h-3.5" /> : '1'}
                      </div>
                      Datos
                    </div>
                    <div className="w-8 h-px bg-white/20" />
                    <div className={`flex items-center gap-1.5 text-sm ${step === 'payment' ? 'text-cyan-400' : 'text-white/30'}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        step === 'payment' ? 'bg-cyan-500/20 border border-cyan-500/50' : 'bg-white/5 border border-white/10'
                      }`}>2</div>
                      Pago
                    </div>
                  </div>

                  {/* Step 1: Register */}
                  {step === 'register' && (
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div>
                        <label className="block text-sm text-white/70 mb-1.5">
                          <User className="inline w-3.5 h-3.5 mr-1" /> Nombre completo *
                        </label>
                        <input
                          type="text"
                          value={fullName}
                          onChange={handleFieldChange(setFullName, 'fullName')}
                          placeholder="Juan Pérez González"
                          className={`w-full bg-white/10 border rounded-lg px-4 py-2.5 text-white placeholder-white/30
                                     focus:outline-none focus:ring-1 ${formErrors.fullName ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400/30' : 'border-white/20 focus:border-cyan-400/50 focus:ring-cyan-400/30'}`}
                        />
                        {formErrors.fullName && <p className="text-red-400 text-xs mt-1">{formErrors.fullName}</p>}
                      </div>

                      <div>
                        <label className="block text-sm text-white/70 mb-1.5">
                          <Mail className="inline w-3.5 h-3.5 mr-1" /> Email *
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={handleFieldChange(setEmail, 'email')}
                          onBlur={handleEmailBlur}
                          placeholder="juan@email.com"
                          className={`w-full bg-white/10 border rounded-lg px-4 py-2.5 text-white placeholder-white/30
                                     focus:outline-none focus:ring-1 ${formErrors.email ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400/30' : 'border-white/20 focus:border-cyan-400/50 focus:ring-cyan-400/30'}`}
                        />
                        {formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}
                      </div>

                      <div>
                        <label className="block text-sm text-white/70 mb-1.5">
                          <FileText className="inline w-3.5 h-3.5 mr-1" /> RUT *
                        </label>
                        <input
                          type="text"
                          value={rut}
                          onChange={handleFieldChange(setRut, 'rut')}
                          onBlur={handleRutBlur}
                          placeholder="12.345.678-9"
                          inputMode="text"
                          className={`w-full bg-white/10 border rounded-lg px-4 py-2.5 text-white placeholder-white/30
                                     focus:outline-none focus:ring-1 ${formErrors.rut ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400/30' : 'border-white/20 focus:border-cyan-400/50 focus:ring-cyan-400/30'}`}
                        />
                        {formErrors.rut && <p className="text-red-400 text-xs mt-1">{formErrors.rut}</p>}
                      </div>

                      <div>
                        <label className="block text-sm text-white/70 mb-1.5">
                          <Phone className="inline w-3.5 h-3.5 mr-1" /> Teléfono *
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={handleFieldChange(setPhone, 'phone')}
                          onBlur={handlePhoneBlur}
                          placeholder="+56 9 1234 5678"
                          inputMode="tel"
                          className={`w-full bg-white/10 border rounded-lg px-4 py-2.5 text-white placeholder-white/30
                                     focus:outline-none focus:ring-1 ${formErrors.phone ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400/30' : 'border-white/20 focus:border-cyan-400/50 focus:ring-cyan-400/30'}`}
                        />
                        {formErrors.phone && <p className="text-red-400 text-xs mt-1">{formErrors.phone}</p>}
                      </div>

                      {actionError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {actionError}
                        </div>
                      )}

                      <motion.button
                        type="submit"
                        disabled={isRegistering}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500
                                   hover:from-cyan-600 hover:to-blue-600
                                   text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/20
                                   disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                      >
                        {isRegistering ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> Registrando...</>
                        ) : (
                          <><ArrowRight className="w-5 h-5" /> Continuar al Pago</>
                        )}
                      </motion.button>
                    </form>
                  )}

                  {/* Step 2: Payment */}
                  {step === 'payment' && (
                    <div>
                      {/* Student summary */}
                      <div className="bg-white/5 rounded-xl p-4 mb-5 border border-white/10">
                        <h3 className="text-sm font-semibold text-white/70 mb-2">Tus datos</h3>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex items-center gap-2 text-slate-300">
                            <User className="w-3.5 h-3.5 text-cyan-400" /> {fullName}
                          </div>
                          <div className="flex items-center gap-2 text-slate-300">
                            <Mail className="w-3.5 h-3.5 text-cyan-400" /> {email}
                          </div>
                          <div className="flex items-center gap-2 text-slate-300">
                            <FileText className="w-3.5 h-3.5 text-cyan-400" /> {rut}
                          </div>
                          <div className="flex items-center gap-2 text-slate-300">
                            <Phone className="w-3.5 h-3.5 text-cyan-400" /> {phone}
                          </div>
                        </div>
                      </div>

                      {actionError && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {actionError}
                        </div>
                      )}

                      {/* Security notice */}
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-4">
                        <ShieldCheck className="w-4 h-4 text-emerald-500" />
                        <span>Pago seguro procesado por WebPay de Transbank</span>
                      </div>

                      <motion.button
                        onClick={handlePayment}
                        disabled={isProcessing}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500
                                   hover:from-emerald-600 hover:to-teal-600
                                   text-white font-bold text-lg rounded-xl
                                   shadow-lg shadow-emerald-500/30
                                   disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                      >
                        {isProcessing ? (
                          <><Loader2 className="w-6 h-6 animate-spin" /> Procesando...</>
                        ) : (
                          <><CreditCard className="w-6 h-6" /> Pagar {formatCLP(linkData.coursePrice)}</>
                        )}
                      </motion.button>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
